import { TextField } from '@material-ui/core';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import React, { useEffect, useRef, useState } from 'react';

import { IBidderGroupLabel } from '~/src/dash/api/bidder-group-label';
import { IChannelGroupLabel } from '~/src/dash/api/channel-group-label';
import { IAdserverAlias, IBidderLabel, IChannelLabel, ISite } from '~/src/dash/api/console-api';
import { EBool } from '~/src/dash/api/types/bool';

interface IProps {
  menuTitle: string;

  // Autocomplete list
  list: Array<IChannelLabel | IChannelGroupLabel | IBidderGroupLabel | IAdserverAlias>;

  // Channel Labels for the channel
  selected: Array<IChannelLabel | IChannelGroupLabel | IBidderGroupLabel | IAdserverAlias | string>;

  // width of the component
  width?: string;

  // disabled turns off opening of menu
  disabled?: boolean;

  // is parent component loading
  loading?: boolean;

  inputLabelProps?: object;

  // disable multi select if true
  disableMultiSelect?: EBool;

  // On change, input is modified list (with .selected set and up to date)
  onChange: (
    values: Array<IChannelLabel | IChannelGroupLabel | IBidderGroupLabel | IAdserverAlias>,
  ) => void;
}

export const MaterialLabelDropdownMulti = (props: IProps) => {
  const { list, menuTitle, onChange, selected, disableMultiSelect } = props;

  const allLabelNames = list.map((label: any) => label.Name);
  const selectedLabelNames = selected.map((label: any) => label.Name || label);

  const [allowFocus, setAllowFocus] = useState(false);
  // inputRef allow us to refocus on the autocomplete input field after each value is entered
  const inputRef = useRef(null as any);
  // at each handleChange, the inputRefFlag will be reset, force focus on the input field
  const [inputRefFlag, setInputRefFlag] = useState([] as string[]);
  useEffect(() => {
    if (inputRef.current && allowFocus) {
      inputRef.current.focus();
    }
  }, [inputRefFlag]);

  const filter = createFilterOptions();
  const [inputValue, setInputValue] = React.useState(selectedLabelNames);

  useEffect(() => {
    const newInputVal = selected.map((label: any) => label.Name || label);
    setInputValue(newInputVal);
  }, [selected]);

  const handleChange = (_: any, value: any) => {
    let lastChange = '';
    setInputRefFlag(value);
    const selectedLabels: string[] = [];
    const newLabels: string[] = [];
    let newSelectedLabels: Array<
      IChannelLabel | IChannelGroupLabel | IBidderGroupLabel | IAdserverAlias | IBidderLabel
    > = [];
    // for the labels passed to this handler, add to selectedLabels if the value is entered via keyboard,
    // or newLabels if the value is clicked on via dropdown (e.g. suggested input such as "Add 'example text'")
    for (const label of value) {
      if (typeof label === 'string') {
        const splitLabels = label.split(',').filter((ele) => ele.length > 0);
        splitLabels.forEach((ele) => {
          selectedLabels.push(ele.trim());
        });
        lastChange = label;
      } else {
        const newLabel = label.inputValue || label.Name;
        if (newLabel) {
          const splitLabels = newLabel.split(',').filter((ele: string) => ele.length > 0);
          splitLabels.forEach((ele: string) => {
            newLabels.push(ele.trim());
          });
        }
        lastChange = newLabel;
      }
    }

    // for each selected label, try to find the label whose name = the current selected label,
    // adding the label to newSelectedLabels if it already exists, and newLabels otherwise
    for (const selectedLabel of selectedLabels) {
      let foundSelectedLabel;
      for (let idx = 0; idx < list.length; idx++) {
        if (list[idx].Name === selectedLabel) {
          foundSelectedLabel = list[idx];
          break;
        }
      }

      if (foundSelectedLabel) {
        newSelectedLabels.push(foundSelectedLabel);
      } else {
        newLabels.push(selectedLabel);
      }
    }

    // for each new label, try to find the label whose name = the new label and adding the label to newSelectedLabels
    for (const newLabel of newLabels) {
      let foundNewLabel;
      for (let idx = 0; idx < list.length; idx++) {
        if (list[idx].Name === newLabel) {
          foundNewLabel = list[idx];
          break;
        }
      }

      // if the label already exists in the list, we can keep it in the new list, else add it in as a new label
      if (foundNewLabel) {
        newSelectedLabels.push(foundNewLabel);
      } else {
        const newChannelLabel = { ID: '0', Name: newLabel, Sites: [] as ISite[] };
        newSelectedLabels.push(newChannelLabel);
      }
    }

    if (disableMultiSelect ? disableMultiSelect === EBool.EBool_TRUE : false) {
      if (lastChange.length > 0) {
        newSelectedLabels = [{ Name: lastChange } as IAdserverAlias];
        setInputValue([lastChange]);
      } else {
        setInputValue([]);
      }
    } else {
      const extractStringNameFromLabels = [] as string[];
      newSelectedLabels.forEach((newSelectedLabel) => {
        extractStringNameFromLabels.push(newSelectedLabel.Name);
      });
      setInputValue(extractStringNameFromLabels);
    }

    onChange(newSelectedLabels);
  };

  return (
    <Autocomplete
      multiple
      id='size-small-standard-multi'
      size='small'
      options={allLabelNames}
      value={inputValue}
      defaultValue={selectedLabelNames}
      disableCloseOnSelect
      filterSelectedOptions
      freeSolo
      style={{
        width: props.width,
      }}
      onChange={(e, val) => {
        if (e.type !== 'blur') {
          handleChange(e, val);
        }
      }}
      onFocus={() => {
        setAllowFocus(true);
      }}
      onInputChange={(e, val) => {
        if (val[val.length - 1] === ',') {
          handleChange(e, [...new Set([...inputValue, val.slice(0, val.length - 1)])]);
        }
      }}
      onBlur={(event: any) => {
        if (event.target.value.length !== 0) {
          handleChange(event, [...new Set([...inputValue, event.target.value])]);
        }
      }}
      autoSelect
      filterOptions={(options, params) => {
        const filtered = filter(options, params);

        // Suggest the creation of a new value
        if (params.inputValue !== '') {
          filtered.push({
            inputValue: params.inputValue,
            title: `Add "${params.inputValue}"`,
          });
        }

        return filtered;
      }}
      getOptionLabel={(option) => {
        // Value selected with enter, right from the input
        if (typeof option === 'string') {
          return option;
        }
        // Add "xxx" option created dynamically
        if (option.inputValue) {
          return option.inputValue;
        }
        // Regular option
        return option.title;
      }}
      getOptionSelected={(option, value) => {
        if (typeof value === 'string') {
          return option === value;
        } else {
          return option === value.inputValue;
        }
      }}
      renderOption={(option) => option.title || option}
      renderInput={(params) => (
        <TextField
          {...params}
          InputLabelProps={props.inputLabelProps}
          inputRef={inputRef}
          variant='standard'
          label={menuTitle}
        />
      )}
    />
  );
};
